import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { rememberReturnPath } from './returnPath';

/**
 * The shell OWNS authentication for the entire micro-frontend platform.
 * Feature apps never talk to Keycloak — they receive tokens through the
 * auth context / api-client only.
 *
 * PKCE (S256) public client against the `platform` realm behind the Nginx edge.
 */
const authority =
  import.meta.env.VITE_OIDC_AUTHORITY ?? 'https://localhost:8443/auth/realms/platform';

export const userManager = new UserManager({
  authority,
  client_id: 'shell-frontend',
  redirect_uri: `${window.location.origin}/callback`,
  post_logout_redirect_uri: `${window.location.origin}/`,
  response_type: 'code',
  scope: 'openid profile email',
  // Session storage: tokens die with the tab; silent renew keeps sessions alive.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  automaticSilentRenew: true,
  // Renew via refresh token (Keycloak issues one for the public client session).
  silentRequestTimeoutInSeconds: 10,
  accessTokenExpiringNotificationTimeInSeconds: 60,
});

export function login(): Promise<void> {
  // The page the sign-in interrupted is the page to come back to - a deep link into a case,
  // not the signpost. Remembered before the redirect; Callback consumes it after.
  rememberReturnPath(
    window.location.pathname + window.location.search + window.location.hash,
    window.sessionStorage
  );
  return userManager.signinRedirect();
}

export async function logout(): Promise<void> {
  try {
    await userManager.signoutRedirect();
  } catch {
    // End-session redirect unavailable (metadata/cert hiccup): drop the local
    // session and land on the shell root rather than leaving a dead spinner.
    await userManager.removeUser();
    window.location.assign('/');
  }
}

/**
 * Exchanges the authorization response for tokens.
 *
 * <p>Takes the response URL explicitly because {@link Callback} scrubs the query string from the
 * address bar before calling this - by then `window.location.href`, the library's default, no
 * longer carries the code.
 */
export function completeLogin(url: string): Promise<User> {
  return userManager.signinRedirectCallback(url);
}

export async function currentUser(): Promise<User | null> {
  const user = await userManager.getUser();
  return user && !user.expired ? user : null;
}

/**
 * Silent session refresh for the api-client's single 401 retry.
 * Returns true when a fresh token is available.
 */
export async function refreshSession(): Promise<boolean> {
  try {
    const user = await userManager.signinSilent();
    return user != null;
  } catch {
    return false;
  }
}

/** Access token supplier for the api-client; refreshes silently when expired. */
export async function getAccessToken(): Promise<string | null> {
  let user = await userManager.getUser();
  if (user && user.expired) {
    try {
      user = await userManager.signinSilent();
    } catch {
      return null;
    }
  }
  return user?.access_token ?? null;
}
